import axios from "axios";
import { bfs } from "@/utils/algorithms";
import { Head, router } from "@inertiajs/vue3";
import { center, centroid } from "@turf/turf";
import { getZiggyRoute } from "@/composables/useZiggyRoute";
import { getPolygonStyles } from "@/utils/polygonStyles";
import { getTenantSetting } from "@/composables/useTenant";
import { registeredMapView } from "@/utils/mapLayers";

export { buildTileRequestUrl, generateMapLayers, getMapLayers, signTileUrl } from "@/utils/mapLayers";
export { getPolygonStyles } from "@/utils/polygonStyles";

const DISTANCE_THRESHOLD = 0.000001;

const bfsCache = new Map();

export const clearBfsCache = () => {
    bfsCache.clear();
};

export const cachedBfs = (target, polygons) => {
    let cacheKey = `${target}`;
    if (bfsCache.has(cacheKey)) {
        return bfsCache.get(cacheKey);
    }

    let result = bfs(polygons, target);
    bfsCache.set(cacheKey, result);

    return result;
};

// given an array of polygons, check if they overlap
export function checkOverlap(polygon, toCheck) {
    let overlapping = [];
    if (!polygon.polygon) return false;

    let firstChild = polygon.centroid;

    toCheck.forEach((child) => {
        if (!child.polygon) return;
        let secondChild = child.centroid;
        console.log("Checking overlap between", firstChild, secondChild);
        let distance = Math.sqrt(
            Math.pow(
                firstChild.coordinates[0] - secondChild.coordinates[0],
                2
            ) +
            Math.pow(
                firstChild.coordinates[1] - secondChild.coordinates[1],
                2
            )
        );
        if (distance < DISTANCE_THRESHOLD) {
            overlapping.push(child);
        }
    });

    return overlapping.length > 0;
}

export function generateMouseHoverMarker(name, limitWidth = true) {
    return {
        html: `<div class="${limitWidth ? "max-w-34 overflow-hidden text-ellipsis  px-4" : "px-6"
            } text-[var(--color-principal)] whitespace-nowrap bg-white font-sans p-2 rounded-full shadow-[0_3px_12px_rgba(0,0,0,0.22)]"><strong>${name}</strong></div>`,
    };
}

export function generateText(text) {
    return {
        html: `<div class=''>
            <p class="whitespace-normal text-center text-[var(--color-principal)]" style="font-size: 0.7rem; font-family: 'Roboto', sans-serif;width: 120px;">${text}</p>
        </div>`,
    };
}

export function chooseTombColor(tomb) {
    let fillColor;
    if (tomb.confirmed_position && tomb.confirmed_measure) {
        fillColor = "#a0a641";
    } else if (tomb.confirmed_position && tomb.confirmed_measure === 0) {
        fillColor = "#ffbd48";
    } else if (tomb.confirmed_position === 0 && tomb.confirmed_measure) {
        fillColor = "#e4623b";
    } else if (tomb.confirmed_position === 0 && tomb.confirmed_measure === 0) {
        fillColor = "#9c533d";
    } else {
        fillColor = "#afa9a1";
    }

    return fillColor;
}

export const currentPolygons = (
    polygons,
    currentNode,
    currentZoom,
    generateMouseHoverMarker,
    currentMap = "medagliere",
    focusOnCurrent = false,
    selectedLeaf = null
) => {
    let result = [];
    let markers = [];

    polygons.value.forEach((polygon) => {
        // the polygons must be added to result, not their children (because there aren't any)
        if (currentMap == "necropoli") {
            let polygonType = polygon.type ? polygon.type.name : polygon.sheet;
            let style = getPolygonStyles(currentZoom.value).benacci[
                polygonType
            ];
            let marker = ["animali"].includes(polygonType)
                ? generateMouseHoverMarker(polygon.text)
                : null;

            if (
                ["campi etichette", "trincee etichette", "note"].includes(
                    polygonType
                )
            ) {
                let marker = {
                    ...polygon,
                    html: generateText(polygon.text).html,
                    iconAnchor: [0, 0],
                    options: {
                        minZoom: 20,
                    },
                };

                markers.push(marker);
            } else {
                result.push({
                    ...polygon,
                    style: style,
                    marker: marker,
                    notInteractive:
                        polygon.id == selectedLeaf || !polygon.sheet,
                    focus: polygon.id == selectedLeaf,
                    sheet: polygon.sheet ? {
                        id: polygon.id,
                        sheet: polygon.sheet,
                        frontend_title: polygon.frontend_title || polygon.name,
                    } : undefined,
                });

                polygon.children?.forEach((child) => {
                    let childStyle = getPolygonStyles(currentZoom.value)
                        .benacci[child.sheet];

                    childStyle.fillColor = chooseTombColor(child);

                    result.push({
                        ...child,
                        style: childStyle,
                        marker: generateMouseHoverMarker(child.map_title),
                        sheet: child.sheet ? {
                            id: child.id,
                            sheet: child.sheet,
                            frontend_title: child.frontend_title || child.map_title,
                        } : undefined,
                    });
                });
            }
        } else {
            if (polygon.children) {
                result.push(
                    ...polygon.children.map((child) => ({
                        ...child,
                        marker: ["Planimetria"].includes(child.type.name)
                            ? null
                            : generateMouseHoverMarker(child.name),
                        style:
                            child.type.name === "Planimetria"
                                ? getPolygonStyles(currentZoom.value)
                                    .planimetry[currentMap]
                                : getPolygonStyles(currentZoom.value)
                                    .depotEntity[currentMap],
                        hoverStyle: null,
                    }))
                );
            }
        }
    });

    if (
        currentNode.value?.parent_id &&
        Object.keys(currentNode.value).length > 0
    ) {
        let currentParentId = currentNode.value.parent_id;
        let parent = cachedBfs(currentParentId, polygons.value);

        let safetyCounter = 0;
        const maxIterations = 20; // max tree depth, to detect potential infinite loops

        while (currentParentId && parent && safetyCounter < maxIterations) {
            if (!result.some((node) => node.id === parent.id)) {
                result.push({
                    ...parent,
                    style: getPolygonStyles(currentZoom.value).depotEntity[
                        currentMap
                    ],
                });
            }

            let parentSiblings = cachedBfs(
                parent.parent_id,
                polygons.value
            )?.children;
            parentSiblings?.forEach((sibling) => {
                if (!result.some((node) => node.id === sibling.id)) {
                    result.push({
                        ...sibling,
                        style: getPolygonStyles(currentZoom.value).depotEntity[
                            currentMap
                        ],
                    });
                }
            });

            currentParentId = parent.parent_id;
            if (currentParentId) {
                parent = cachedBfs(currentParentId, polygons.value);
            }
            safetyCounter++;
        }

        if (safetyCounter >= maxIterations) {
            console.error(
                "Safety counter reached, potential infinite loop detected."
            );
        }

        let lastParentSiblings = cachedBfs(
            parent.parent_id,
            polygons.value
        )?.children?.filter((c) => c.id !== parent.id);
        lastParentSiblings?.forEach((sibling) => {
            if (!result.some((node) => node.id === sibling.id)) {
                result.push({
                    ...sibling,
                    style: getPolygonStyles(currentZoom.value).depotEntity[
                        currentMap
                    ],
                });
            }
        });

        result.push({
            ...currentNode.value,
            focus: focusOnCurrent,
            style: getPolygonStyles(currentZoom.value).depotEntity[currentMap],
            hoverStyle: getPolygonStyles(currentZoom.value).hoverChild[
                currentMap
            ],
        });

        let siblings = cachedBfs(
            currentNode.value.parent_id,
            polygons.value
        )?.children?.filter((c) => c.id !== currentNode.value.id);

        siblings?.forEach((sibling) => {
            if (!result.some((node) => node.id === sibling.id)) {
                result.push({
                    ...sibling,
                    style: getPolygonStyles(currentZoom.value).depotEntity[
                        currentMap
                    ],
                    marker: ["Planimetria"].includes(sibling.type?.name)
                        ? null
                        : generateMouseHoverMarker(sibling.name),
                });
            }
        });

        currentNode.value.children?.forEach((child) => {
            let temp = {};

            if (
                checkOverlap(
                    child,
                    currentNode.value.children.filter((c) => c.id !== child.id)
                )
            ) {
                temp.notInteractive = true;
            } else {
                temp.hoverStyle = getPolygonStyles(
                    currentZoom.value
                ).hoverChild[currentMap];
            }
            result.push({
                ...child,
                ...temp,
                style: child.children
                    ? getPolygonStyles(currentZoom.value).depotEntity[
                    currentMap
                    ]
                    : getPolygonStyles(currentZoom.value).furniture[currentMap],
                marker: ["Planimetria"].includes(child.type?.name)
                    ? null
                    : generateMouseHoverMarker(child.name),
            });
        });
    }

    result.forEach((p) => {
        p.sheet = {
            id: p.id,
            depth: p.depth,
            sheet: p.sheet,
            parentId: p.parent_id,
            title: p.name,
        };

        if (p.polygon && !p.notInteractive) {
            let parentNode = cachedBfs(p.parent_id, polygons.value);
            let isCurrentNode = currentNode.value?.id === p.id;
            let isLeafNode = !p.children || p.children.length === 0;

            if (isCurrentNode && isLeafNode) {
                p.style = getPolygonStyles(currentZoom.value).hoverChild[
                    currentMap
                ];
                p.marker = generateMouseHoverMarker(p.name);
                p.hoverStyle = null;
                return;
            }

            let disabledMarker = isCurrentNode || p.disableMarker;

            if (currentNode.value && parentNode) {
                let currentParent = cachedBfs(
                    currentNode.value.parent_id,
                    polygons.value
                );
                if (currentParent) {
                    disabledMarker =
                        disabledMarker || p.depth >= currentParent.depth;
                }
            }

            if (disabledMarker) {
                p.marker = null;
                p.hoverStyle = null;
            } else if (!p.hoverStyle) {
                // if hoverStyle hasn't been set previously, overwrite it with the default value
                p.hoverStyle = getPolygonStyles(currentZoom.value).hoverChild[
                    currentMap
                ];
            }
        }

        if (p.polygon && !p.centroid) {
            p.centroid = centroid(JSON.parse(p.polygon));
        }
    });

    // remove elements with name == 'Fondo' from the results
    if (currentMap == "museo") {
        result = result.filter((r) => r.name !== "Fondo");
    }

    return {
        polygons: result,
        markers: markers,
    };
};

export const handleClick = async (
    e,
    currentNode,
    loadingPolygons,
    polygons,
    inSearchResults,
    loadChildrenRelations = false
) => {
    const isActuallySearching = inSearchResults && !e.fromLeftMenu;

    if (isActuallySearching) {
        loadingPolygons.value = false;

        if (!e.polygons || e.polygons.length === 0) {
            return;
        }

        let minDepth = Number.MAX_SAFE_INTEGER;
        let chosenId = null;

        e.polygons.forEach((polygon) => {
            if (polygon.data.depth < minDepth) {
                minDepth = polygon.data.depth;
                chosenId = polygon.data.id;
            }
        });

        if (chosenId) {
            currentNode.value = bfs(polygons.value, chosenId);
        }
    } else {
        let tempCurrentNode = { ...currentNode.value };
        let minimumDepth = Number.MAX_SAFE_INTEGER,
            minimumId = null;
        let filteredPolygons = e.polygons.filter(
            (p) => p.data.id != currentNode.value?.id || !currentNode.value
        );

        loadingPolygons.value = true;

        let parents = [];
        let siblings = [];

        filteredPolygons.forEach((polygon) => {
            if (!polygon.data.parent_id) return;

            if (
                polygon.data.parent_id &&
                parents.includes(polygon.data.parent_id)
            ) {
                siblings.push(polygon);
                siblings.push(
                    filteredPolygons.find(
                        (p) => p.data.parent_id == polygon.data.parent_id
                    )
                );
            } else {
                parents.push(polygon.data.parent_id);
            }
        });

        if (siblings.length > 0) {
            // show popup
        } else {
            filteredPolygons.forEach((polygon) => {
                if (
                    polygon.data.depth >= 1 &&
                    polygon.data.depth < minimumDepth
                ) {
                    minimumDepth = polygon.data.depth;
                    minimumId = polygon.data.id;
                }
            });

            if (minimumDepth < Number.MAX_SAFE_INTEGER) {
                tempCurrentNode = cachedBfs(minimumId, polygons.value);
            }
        }

        if (
            tempCurrentNode &&
            typeof tempCurrentNode.children === "undefined"
        ) {
            try {
                const url = `/api/polygons/${tempCurrentNode.id}/children${loadChildrenRelations ? "?relations=true" : ""
                    }`;
                const res = await axios.get(url);
                tempCurrentNode.children = res.data.data;
            } catch (error) {
                console.error("Error loading children:", error);
            }
        }

        loadingPolygons.value = false;
        if (tempCurrentNode) {
            currentNode.value = tempCurrentNode;
        }
    }
};

export const resetHandler = (resetRoute, polygons, currentNode) => {
    const route = getZiggyRoute();
    const url = route(resetRoute);
    console.log("Resetting polygons to:", url);

    router.visit(url, {
        preserveState: false,
        preserveScroll: false,
        replace: true,
        only: ["polygons"],
        onSuccess: (page) => {
            clearBfsCache();
            polygons.value = page.props.polygons;
            currentNode.value =
                polygons.value.length > 1 ? null : polygons.value[0];
        },
    });
};

export const LEAFLET_CONFIG = {
    get center() {
        const registered = registeredMapView().center;
        if (registered) return registered;
        const value = getTenantSetting('map.center');
        try { const point = typeof value === 'string' ? JSON.parse(value) : value; if (Array.isArray(point) && point.length === 2 && point.every(Number.isFinite)) return point; } catch { /* Legacy missing or malformed setting. */ }
        return [0, 0];
    },
    get zoom() { return registeredMapView().zoom; },
    get minZoom() { return registeredMapView().minZoom ?? parseIntegerSetting(getTenantSetting("map.min_zoom"), 20); },
    get maxZoom() { return registeredMapView().maxZoom ?? parseIntegerSetting(getTenantSetting("map.max_zoom"), 25); },
    get maxNativeZoom() { return registeredMapView().maxNativeZoom ?? parseIntegerSetting(getTenantSetting("map.max_native_zoom"), 24); },
    get fitBoundsPadding() {
        const padding = getTenantSetting("map.fit_bounds_padding");
        return registeredMapView().fitBoundsPadding ?? (padding ? parseFloat(padding) : 0.005);
    },
    get rotate() { return registeredMapView().rotate ?? (getTenantSetting("map.rotate") == "true"); },
    rotateControl: false,
    get maxBounds() {
        const bounds = getTenantSetting("map.max_bounds");
        return registeredMapView().maxBounds ?? (bounds ? JSON.parse(bounds) : null);
    },
    get bearing() { return registeredMapView().bearing ?? getTenantSetting("map.bearing") ?? 0; },
};

export const pinToPolygon = (pin, color = null, clickCallback = null) => {
    let result = {
        ...pin,
        options: {
            minZoom: 12,
        },
        tooltip: pin.notes,
        polygon: `{ "type" : "Feature", "geometry" : { "type" : "Point", "coordinates" : [${pin.lng}, ${pin.lat}] } }`,
        order: pin.bigger ? 10000 : 100,
        onClick: clickCallback,

        style: {
            fillColor: color ? color : "#FF0000",
            fillOpacity: pin.opacity ? pin.opacity : 1,
            color: "#000000",
            opacity: pin.opacity ? pin.opacity : 1,
            radius: pin.bigger ? 10 : 7,
            weight: 1,
            zIndex: 100000000,
        },
    };

    return result;
};

function parseIntegerSetting(value, fallback) {
    const parsedValue = parseInt(value, 10);

    return Number.isFinite(parsedValue) ? parsedValue : fallback;
}
